import { computed, inject, toValue, type MaybeRefOrGetter } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { personalityQueryOptions } from '@/api/queries/static-data/personality.query';
import type { Avatar as AvatarFn } from '@/features/avatar/types';
import type { User, UserPublic } from '@/api/queries/user/types/user';

type ImgSize = 'card' | 'large' | 'medium' | 'mini' | 'small' | 'thumb';
type AvatarUser = User | UserPublic | null | undefined;

export function useALAvatar(
  user: MaybeRefOrGetter<AvatarUser>,
  options: { imgSize?: ImgSize } = {},
) {
  const avatar = inject<AvatarFn | null>('avatar', null);
  const { data: personalityGradients } = useQuery(personalityQueryOptions);

  const userInfo = computed(() => toValue(user));

  const firstName = computed(() => userInfo.value?.first_name ?? userInfo.value?.firstname ?? '');
  const lastName = computed(() => userInfo.value?.last_name ?? userInfo.value?.lastname ?? '');

  const avatarUrl = computed(() => {
    const id = userInfo.value?.branding?.id;
    if (!id || !avatar) return '';
    return avatar(id, options.imgSize ?? 'card') || '';
  });

  const gradients = computed(() => {
    const discId = userInfo.value?.disc_id;
    if (!discId) return undefined;
    return personalityGradients.value?.[discId]?.gradients;
  });

  return { avatarUrl, firstName, lastName, gradients };
}
